import { ButtonPrimary } from 'components/Button'
import { AutoColumn } from 'components/Column'
import Modal from 'components/Modal'
import Row, { RowBetween } from 'components/Row'
import { useContext, useEffect, useRef, useState } from 'react'
import { Twitter, X } from 'react-feather'
import styled, { ThemeContext } from 'styled-components'
import { CloseIcon, ThemedText } from 'theme'

const Wrapper = styled.div`
  width: 100%;
  padding: 1rem;
`

const Canvas = styled.canvas`
  display: none;
`

const ImagePreview = styled.img`
  width: 100%;
  border-radius: 12px;
  margin-bottom: 20px;
`

const HeaderRow = styled(RowBetween)`
  margin-bottom: 20px;
`

interface ShareModalProps {
  isOpen: boolean
  onDismiss: () => void
  amount: string
}

export default function ShareModal({ isOpen, onDismiss, amount }: ShareModalProps) {
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen || !canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = async () => {
      canvas.width = img.width
      canvas.height = img.height

      ctx.drawImage(img, 0, 0)

      const fontSize = 96
      const fontFamily = 'Outfit custom, Outfit, Inter, sans-serif'
      const fontWeight = 600
      const letterSpacing = 1.92
      const text = amount
      const textX = Math.round(canvas.width * 0.049)
      const textY = Math.round(canvas.height * 0.22)

      if (document.fonts?.load) {
        try {
          await document.fonts.load(`${fontWeight} ${fontSize}px ${fontFamily}`)
        } catch {
          // Font loading is best-effort for canvas rendering.
        }
      }

      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`
      ctx.textAlign = 'left'
      ctx.textBaseline = 'top'

      const textWidth =
        Array.from(text).reduce((acc, char) => acc + ctx.measureText(char).width, 0) +
        Math.max(0, text.length - 1) * letterSpacing

      const gradient = ctx.createLinearGradient(textX, textY, textX + textWidth, textY)
      gradient.addColorStop(0, '#FFFFFF')
      gradient.addColorStop(0.0616, '#58B3FF')
      gradient.addColorStop(0.1185, '#A6FFFF')
      gradient.addColorStop(0.3257, '#FFFFFF')
      gradient.addColorStop(0.7234, '#D0FFFF')
      gradient.addColorStop(0.849, '#FFFFFF')
      gradient.addColorStop(0.9684, '#65B9FF')

      ctx.shadowColor = 'rgba(255, 255, 255, 0.5)'
      ctx.shadowBlur = 12
      ctx.fillStyle = gradient

      let cursorX = textX
      for (const char of Array.from(text)) {
        ctx.fillText(char, cursorX, textY)
        cursorX += ctx.measureText(char).width + letterSpacing
      }

      ctx.shadowBlur = 0
      ctx.shadowColor = 'transparent'

      setImageUrl(canvas.toDataURL('image/png'))
    }

    img.onerror = (err) => {
      console.error('Failed to load share template image', err)
    }

    img.src = `/images/claim-share.png`
  }, [isOpen, amount, canvas])

  const theme = useContext(ThemeContext)

  const handleDownload = () => {
    if (imageUrl) {
      const link = document.createElement('a')
      link.download = 'xkdk-claim.png'
      link.href = imageUrl
      link.click()
    }
  }

  const handleShareTwitter = () => {
    const text = `I just claimed ${amount} xKDK on Kodiak! 🐻\n\nCheck it out at https://app.kodiak.finance`
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`
    window.open(url, '_blank')
  }

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={90} maxWidth={420} initialFocusRef={wrapperRef}>
      <Wrapper ref={wrapperRef} tabIndex={-1} style={{ outline: 'none' }}>
        <HeaderRow>
          <ThemedText.DeprecatedMediumHeader>Share your Claim</ThemedText.DeprecatedMediumHeader>
          <CloseIcon onClick={onDismiss}>
            <X size={24} />
          </CloseIcon>
        </HeaderRow>

        <Canvas ref={setCanvas} />

        {imageUrl ? (
          <ImagePreview src={imageUrl} alt="Claim Preview" />
        ) : (
          <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            Loading preview...
          </div>
        )}

        <Row gap="2px">
          <ButtonPrimary $borderRadius="4px 0px 0px 4px" width="90%" nowrap onClick={handleDownload}>
            Download Image
          </ButtonPrimary>
          <ButtonPrimary
            height="auto"
            lineHeight="8px"
            fontSize="12px"
            $borderRadius="0px 4px 4px 0px"
            width="20%"
            onClick={handleShareTwitter}
          >
            <AutoColumn gap="4px" justify="center">
              <Twitter size="12px" fill="white" />
              <ThemedText.DeprecatedBody fontSize={12}>Share</ThemedText.DeprecatedBody>
            </AutoColumn>
          </ButtonPrimary>
        </Row>
      </Wrapper>
    </Modal>
  )
}
